/**
 * MultiPartQuestionCard Component
 * Renders multi-part diagnostic questions with conditional sub-questions
 * and automatic compliance scoring
 */

import { useState, useEffect, useMemo } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  HelpCircle,
  Info,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Phone,
} from 'lucide-react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { cn } from '../../lib/utils';
import type { ChecklistStatus } from '../../types';
import type {
  DiagnosticQuestionConfig,
  SubQuestion,
  SubQuestionAnswer,
  EvaluationResult,
} from '../../types/diagnostic';
import { evaluateDiagnosticAnswers } from '../../config/diagnostic/generator';

interface MultiPartQuestionCardProps {
  question: DiagnosticQuestionConfig;
  answers: SubQuestionAnswer[];
  onChange: (answers: SubQuestionAnswer[]) => void;
  onFocus?: () => void;
  showStatus?: boolean;
  isCardExpanded?: boolean;
}

const STATUS_CONFIG: Record<
  ChecklistStatus,
  {
    icon: React.ElementType;
    color: string;
    bgColor: string;
    borderColor: string;
    label: string;
  }
> = {
  COMPLIANT: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    label: 'Compliant',
  },
  AT_RISK: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    label: 'At Risk',
  },
  NON_COMPLIANT: {
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Non-Compliant',
  },
  UNKNOWN: {
    icon: HelpCircle,
    color: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    label: 'Not Answered',
  },
};

export function MultiPartQuestionCard({
  question,
  answers,
  onChange,
  onFocus,
  showStatus = true,
  isCardExpanded = true,
}: MultiPartQuestionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [evaluation, setEvaluation] = useState<EvaluationResult>({
    status: 'UNKNOWN',
    reportText: question.reportLanguage.unknown,
  });

  // Create answer map for easy lookup
  const answerMap = useMemo(() => {
    const map = new Map<string, string | number | boolean | string[] | null>();
    answers.forEach((a) => map.set(a.questionId, a.value));
    return map;
  }, [answers]);

  // Evaluate status when answers change
  useEffect(() => {
    const result = evaluateDiagnosticAnswers(question, answers);
    setEvaluation(result);
  }, [answers, question]);

  // Auto-expand regulation reference when user answers a question
  useEffect(() => {
    if (answers.length > 0 && !isExpanded) {
      setIsExpanded(true);
    }
  }, [answers.length]);

  // Check if a sub-question should be visible based on conditions
  const isQuestionVisible = (subQ: SubQuestion): boolean => {
    if (!subQ.showCondition) return true;

    const { questionId, operator, value } = subQ.showCondition;
    const parentAnswer = answerMap.get(questionId);

    switch (operator) {
      case 'equals':
        return parentAnswer === value;
      case 'notEquals':
        return parentAnswer !== value;
      case 'includes':
        if (Array.isArray(parentAnswer) && typeof value === 'string') {
          return parentAnswer.includes(value);
        }
        if (Array.isArray(value)) {
          return value.includes(parentAnswer as string);
        }
        return false;
      case 'notIncludes':
        if (Array.isArray(parentAnswer) && typeof value === 'string') {
          return !parentAnswer.includes(value);
        }
        if (Array.isArray(value)) {
          return !value.includes(parentAnswer as string);
        }
        return true;
      case 'exists':
        return parentAnswer !== null && parentAnswer !== undefined && parentAnswer !== '';
      case 'notExists':
        return parentAnswer === null || parentAnswer === undefined || parentAnswer === '';
      case 'greaterThan':
        return typeof parentAnswer === 'number' && parentAnswer > (value as number);
      case 'lessThan':
        return typeof parentAnswer === 'number' && parentAnswer < (value as number);
      default:
        return true;
    }
  };

  // Update a single answer
  const updateAnswer = (questionId: string, value: string | number | boolean | string[] | null) => {
    const existingIndex = answers.findIndex((a) => a.questionId === questionId);
    const newAnswer: SubQuestionAnswer = { questionId, value };

    let newAnswers: SubQuestionAnswer[];
    if (existingIndex >= 0) {
      newAnswers = [...answers];
      newAnswers[existingIndex] = newAnswer;
    } else {
      newAnswers = [...answers, newAnswer];
    }

    onChange(newAnswers);
    // Notify parent to expand this card
    onFocus?.();
  };

  const statusConfig = STATUS_CONFIG[evaluation.status];
  const StatusIcon = statusConfig.icon;

  // Get visible questions
  const visibleQuestions = question.questions.filter(isQuestionVisible);
  const hasAnsweredFirst =
    answers.length > 0 && answerMap.get(question.questions[0]?.id) !== undefined;

  return (
    <div
      className={cn(
        'border-2 rounded-xl transition-all overflow-hidden',
        showStatus && evaluation.status !== 'UNKNOWN'
          ? `${statusConfig.bgColor} ${statusConfig.borderColor}`
          : 'border-gray-200 bg-white'
      )}
    >
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start gap-4 mb-4">
          <span className="flex-shrink-0 min-w-[3rem] h-10 px-3 bg-gray-100 rounded-full flex items-center justify-center text-sm font-bold text-gray-700 whitespace-nowrap">
            {question.itemId}
          </span>
          <div className="flex-1">
            <h3 className="text-gray-900 font-semibold text-lg">{question.title}</h3>
            {question.description && (
              <p className="text-sm text-gray-600 mt-1">{question.description}</p>
            )}
          </div>
          <span
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full uppercase',
              question.severity === 'high'
                ? 'bg-red-100 text-red-700'
                : question.severity === 'medium'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-blue-100 text-blue-700'
            )}
          >
            {question.severity}
          </span>
        </div>

        {/* Sub-questions */}
        <div className="space-y-4">
          {visibleQuestions.map((subQ, index) => (
            <div key={subQ.id} className="space-y-2">
              {/* Question label */}
              <div className="flex items-start gap-2">
                <span className="text-sm font-medium text-gray-500">Q{index + 1}.</span>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium">{subQ.question}</p>
                  {subQ.helpText && (
                    <p className="text-sm text-gray-500 mt-1 flex items-start gap-1">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {subQ.helpText}
                    </p>
                  )}
                </div>
              </div>

              {/* Input */}
              <div className="ml-6">
                {renderSubQuestionInput(
                  subQ,
                  answerMap.get(subQ.id),
                  (value) => updateAnswer(subQ.id, value)
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Status indicator - only show after answering and when card is expanded */}
        {showStatus && isCardExpanded && hasAnsweredFirst && evaluation.status !== 'UNKNOWN' && (
          <div
            className={cn(
              'mt-4 p-4 rounded-lg',
              evaluation.status === 'COMPLIANT' && 'bg-green-100',
              evaluation.status === 'AT_RISK' && 'bg-yellow-100',
              evaluation.status === 'NON_COMPLIANT' && 'bg-red-100'
            )}
          >
            <div className="flex items-start gap-3">
              <StatusIcon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', statusConfig.color)} />
              <div className="flex-1">
                <span className={cn('font-semibold', statusConfig.color)}>
                  {statusConfig.label}
                </span>
                <p className="text-sm text-gray-700 mt-1">{evaluation.reportText}</p>
                {evaluation.missingItems && evaluation.missingItems.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-600">Missing items:</p>
                    <ul className="text-sm text-gray-600 list-disc list-inside mt-1">
                      {evaluation.missingItems.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Expandable footer with regulation info - only show when card is expanded */}
      {isCardExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-5 py-3 flex items-center justify-between text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span>
              Ref: {question.regulationCode} — {question.agencyAbbr}
            </span>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {isExpanded && (
          <div className="px-5 pb-4 space-y-2 text-sm text-gray-600">
            <p>
              <strong>Regulation:</strong> {question.regulationCode}
            </p>
            <p>
              <strong>Name:</strong> {question.regulationName}
            </p>
            <p>
              <strong>Agency:</strong> {question.agencyName} ({question.agencyAbbr})
            </p>
            {question.agencyPhone && (
              <p className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                <a href={`tel:${question.agencyPhone}`} className="text-teal-600 hover:underline">
                  {question.agencyPhone}
                </a>
              </p>
            )}
            {question.agencyUrl && (
              <p className="flex items-center gap-1">
                <ExternalLink className="w-4 h-4" />
                <a
                  href={question.agencyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:underline"
                >
                  Agency Website
                </a>
              </p>
            )}
            {question.stateNotes && question.stateNotes.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="font-medium text-gray-700">State-specific notes:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {question.stateNotes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        </div>
      )}
    </div>
  );
}

/**
 * Render the appropriate input type for a sub-question
 */
function renderSubQuestionInput(
  question: SubQuestion,
  value: string | number | boolean | string[] | null | undefined,
  onChange: (value: string | number | boolean | string[] | null) => void
) {
  switch (question.inputType) {
    case 'binary':
      return (
        <div className="flex gap-3">
          {(question.options || [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]).map(
            (option) => (
              <button
                key={option.value}
                onClick={() => onChange(option.value)}
                className={cn(
                  'flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all text-sm',
                  value === option.value
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                )}
              >
                {option.label}
                {option.description && (
                  <span className="block text-xs font-normal mt-1 opacity-75">
                    {option.description}
                  </span>
                )}
              </button>
            )
          )}
        </div>
      );

    case 'select':
      return (
        <Select
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value || null)}
          options={[
            { value: '', label: question.placeholder || 'Select an option...' },
            ...(question.options || []),
          ]}
          className="max-w-md"
        />
      );

    case 'multiselect':
      const multiValues = (value as string[]) || [];
      return (
        <div className="space-y-2 max-w-md">
          <Select
            value=""
            onChange={(e) => {
              if (e.target.value && !multiValues.includes(e.target.value)) {
                onChange([...multiValues, e.target.value]);
              }
            }}
            options={[
              { value: '', label: question.placeholder || 'Select to add...' },
              ...(question.options || []).filter((o) => !multiValues.includes(o.value)),
            ]}
          />
          {multiValues.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {multiValues.map((v) => {
                const opt = question.options?.find((o) => o.value === v);
                return (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                  >
                    {opt?.label || v}
                    <button
                      onClick={() => onChange(multiValues.filter((x) => x !== v))}
                      className="hover:text-teal-900"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      );

    case 'checklist':
      const selectedValues = (value as string[]) || [];
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(question.options || []).map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => {
                  const newValues = isSelected
                    ? selectedValues.filter((v) => v !== option.value)
                    : [...selectedValues, option.value];
                  onChange(newValues);
                }}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all',
                  isSelected
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                )}
              >
                <div
                  className={cn(
                    'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                    isSelected ? 'border-teal-500 bg-teal-500' : 'border-gray-300 bg-white'
                  )}
                >
                  {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <span
                    className={cn(
                      'text-sm font-medium',
                      isSelected ? 'text-teal-700' : 'text-gray-700'
                    )}
                  >
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="block text-xs text-gray-500 mt-1">{option.description}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      );

    case 'number':
      return (
        <div className="flex items-center gap-3 max-w-xs">
          <Input
            type="number"
            placeholder={question.placeholder || 'Enter a number'}
            value={(value as number) || ''}
            onChange={(e) => {
              const num = e.target.value ? parseFloat(e.target.value) : null;
              onChange(num);
            }}
          />
          {question.unit && <span className="text-gray-500 font-medium">{question.unit}</span>}
        </div>
      );

    case 'date':
      return (
        <Input
          type="date"
          placeholder={question.placeholder}
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="max-w-xs"
        />
      );

    case 'text':
      return (
        <Input
          type="text"
          placeholder={question.placeholder || 'Enter your answer'}
          value={(value as string) || ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="max-w-md"
        />
      );

    default:
      return null;
  }
}
